/**
 * Reference badges of the whole fleet for the Dashboard cards: one REST call every
 * 10 s (plus Socket.IO `reference_badges`) shared by every card through a provider.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';

import { referencesApi } from '../services/referencesApi';
import { tokenStorage } from '../services/httpClient';
import { Badge, Badges } from '../types/references';

export const BADGES_POLL_MS = 10_000;

const SOCKET_URL = (process.env.REACT_APP_SOCKET_URL as string) || window.location.origin;

// null outside the provider: cards rendered elsewhere show no badge
const ReferenceBadgesContext = createContext<Badges | null>(null);

export const ReferenceBadgesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [badges, setBadges] = useState<Badges>({});

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const data = await referencesApi.getBadges();
        if (!cancelled) setBadges(data);
      } catch (e) {
        // Keep the last badges: the next poll retries
      }
    };

    load();
    const timer = setInterval(load, BADGES_POLL_MS);

    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      path: '/socket.io',
      autoConnect: !!tokenStorage.getAccess(),
      auth: (cb) => cb({ token: tokenStorage.getAccess() || '' }),
    });
    socket.on('reference_badges', (data: Badges) => {
      if (!cancelled && data && typeof data === 'object') setBadges(data);
    });

    return () => {
      cancelled = true;
      clearInterval(timer);
      socket.disconnect();
    };
  }, []);

  return <ReferenceBadgesContext.Provider value={badges}>{children}</ReferenceBadgesContext.Provider>;
};

export function useReferenceBadge(deviceId: number): Badge {
  const badges = useContext(ReferenceBadgesContext);
  return badges?.[String(deviceId)] ?? null;
}
